import { Injectable } from '@angular/core';
import { WebAPIClient } from './http-services/http-service';
import { OrderData, OrderState, SpecialTicketData } from '../shared/models/order';
import { BehaviorSubject, catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { PassengerType } from '../shared/models/passenger-types';
import { BackendError } from './error-service';
import { HttpErrorResponse } from '@angular/common/http';
import { LocalizedDataUtility } from './utility/localized-data.util';

@Injectable({ providedIn: 'root' })
export class OrderService {
    private orderDataSubject = new BehaviorSubject<OrderData | null>(null);
    public orderData$ = this.orderDataSubject.asObservable();

    constructor(
        private webAPIClient: WebAPIClient,
        private localizedDataUtility: LocalizedDataUtility
    ) { }

    getOrder(orderId: string): Observable<OrderData> {
        if (!orderId || orderId.trim().length === 0) {
            return throwError(() => ({
                Id: 'REJSEBILLET.WEB.GENERIC_ERROR',
                operationId: undefined
            } as BackendError));
        }

        const cached = this.orderDataSubject.getValue();

        if (cached && cached.id === orderId) {
            return of(cached);
        }

        return this.webAPIClient.get<OrderData>(`/orders/${orderId}`, { withCredentials: true }).pipe(
            map(data => this.mapOrderData(data)),
            tap(order => {
                this.orderDataSubject.next(order);
            }),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => this.handleError(error));
            })
        );
    }

    getCurrentOrder(): OrderData | null {
        return this.orderDataSubject.getValue();
    }

    private mapOrderData(data: any): OrderData {
        try {
            return {
                id: data.id || '',
                ticketNumber: data.ticketNumber || '',
                price: data.price || 0,
                status: data.status || OrderState.Unknown,
                base64Aztec: data.base64Aztec || '',
                purchaseDate: new Date(data.purchaseDate),
                issueDate: new Date(data.issueDate),
                validFrom: new Date(data.validFrom),
                validTo: new Date(data.validTo),
                specialTicket: this.mapSpecialTicket(data.specialTicket),
                validZones: Array.isArray(data.validZones) ? data.validZones : [],
                passengers: Array.isArray(data.passengers)
                    ? data.passengers.map((p: any) => ({
                        type: this.mapPassengerType(p.type),
                        count: p.count || 0
                    }))
                    : []
            };
        } catch (error) {
            console.error('Error transforming order data:', error);
            throw error;
        }
    }

    private mapSpecialTicket(rawTicket: any): SpecialTicketData | null {
        if (!rawTicket) return null;

        const ticket: SpecialTicketData = {
            id: rawTicket.id || '',
            ticketName: rawTicket.ticketName || {},
            ticketDescription: rawTicket.ticketDescription || {},
            ticketValidityText: rawTicket.ticketValidityText || {},
            overriddenPassengerTypes: rawTicket.overriddenPassengerTypes || []
        };

        return this.localizedDataUtility.extractLocalizedData(
            ticket,
            ['ticketName', 'ticketDescription', 'ticketValidityText']
        );
    }

    private mapPassengerType(value: string): PassengerType {
        switch (value?.toUpperCase()) {
            case 'A': return PassengerType.Adult;
            case 'S': return PassengerType.Senior;
            case 'C': return PassengerType.Child;
            case 'B': return PassengerType.Bike;
            case 'H': return PassengerType.Handicapped;
            case 'Y': return PassengerType.Young;
            case 'D': return PassengerType.Dog;
            default: return PassengerType.Unknown;
        }
    }

    private handleError(error: HttpErrorResponse): BackendError {
        const backendError: BackendError = {
            Id: error.error?.Id ?? 'REJSEBILLET.WEB.GENERIC_ERROR',
            operationId: error.headers.get('X-Operation-ID') ?? undefined
        };

        console.error('Order service error:', backendError);
        return backendError;
    }
}
